import { Database, BarChart3, Brain, Workflow, ShieldCheck, Scale, FileSearch, LineChart } from "lucide-react";

const B2B = [
  { icon: Database, title: "Data Engineering", desc: "Lakehouses, pipelines, and warehouse modernization on Snowflake, Databricks, BigQuery." },
  { icon: BarChart3, title: "Embedded BI", desc: "Self-serve dashboards and metric stores wired into your operating cadence." },
  { icon: Brain, title: "Predictive Models", desc: "Forecasting, churn, pricing, and demand models — moved from notebook to production." },
  { icon: Workflow, title: "Decision Ops", desc: "Closed-loop automation that turns analysis into reliable business actions." },
];

const B2G = [
  { icon: ShieldCheck, title: "FedRAMP-aligned Stacks", desc: "Reference architectures with audit trails, encryption, and SBOM by default." },
  { icon: Scale, title: "Policy & Compliance Analytics", desc: "Evidence packages for GAO, OMB, and program review cycles." },
  { icon: FileSearch, title: "Public-Sector Intelligence", desc: "Open-source intel fusion, geospatial, and situational dashboards." },
  { icon: LineChart, title: "Procurement & Spend", desc: "Category-level analytics for vendor performance and contract risk." },
];

function Card({ icon: Icon, title, desc, idx }) {
  return (
    <div
      data-testid={`service-card-${idx}`}
      className="group relative p-8 lg:p-10 border-r border-b border-neutral-200 bg-white hover:bg-neutral-50 transition-colors"
    >
      <div className="flex items-start justify-between mb-12">
        <Icon className="w-7 h-7 text-black" strokeWidth={1.5} />
        <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-400">
          /0{idx + 1}
        </span>
      </div>
      <h3 className="font-display text-2xl font-bold tracking-tight">{title}</h3>
      <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{desc}</p>
      <div className="mt-8 h-px w-8 bg-black group-hover:w-full transition-all duration-500" />
    </div>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="border-b border-neutral-200 bg-white">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-8 mb-16">
          <div className="col-span-12 lg:col-span-5">
            <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-500">
              [03] Services
            </span>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Two practices.
              <br />
              One <span className="text-[#002FA7]">operating discipline</span>.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-neutral-700 leading-relaxed max-w-xl">
              Our commercial and federal teams share the same engineering bench. Methods that pass a
              GAO audit also clear a board review — fewer toolchains, less bespoke risk.
            </p>
          </div>
        </div>

        {/* B2B block */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <span className="inline-block w-2 h-2 bg-[#002FA7]" />
            <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-700">
              B2B / Enterprise practice
            </span>
            <span className="h-px flex-1 bg-neutral-200" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-neutral-200">
            {B2B.map((s, i) => (
              <Card key={s.title} {...s} idx={i} />
            ))}
          </div>
        </div>

        {/* B2G block */}
        <div>
          <div className="flex items-center gap-4 mb-6">
            <span className="inline-block w-2 h-2 bg-black" />
            <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-700">
              B2G / Public-sector practice
            </span>
            <span className="h-px flex-1 bg-neutral-200" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-neutral-200">
            {B2G.map((s, i) => (
              <Card key={s.title} {...s} idx={i + 4} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
